import { useEffect, useRef, useState } from 'react';

/*
  Grows one block inside the side column (the attached files list) so the side column ends at the same height as
  the main column next to it, instead of leaving an empty grey area below it.

  Each column holds a hidden marker element; its parent (the Polaris Layout.Section) is the column that is watched
  with a ResizeObserver. The block is found in the side column with `blockSelector` and measured, so its target
  height is: main column height - (side column height - block height), never less than the default. When the
  columns are stacked (narrow screen) or the hook is disabled, the default height is used.
  A block the user resized by hand keeps its size: dragging changes the block and the side column by the same
  amount, so the target stays the same and React does not touch the height.
*/
export function useFillColumnHeight({ defaultHeight, blockSelector, enabled = true }) {
  // callback refs (state) so the observer attaches once the columns render, the page shows a loader before that
  const [mainColumnMarker, mainColumnMarkerRef] = useState(null);
  const [sideColumnMarker, sideColumnMarkerRef] = useState(null);
  const heightRef = useRef(defaultHeight);
  const [height, setHeight] = useState(defaultHeight);

  useEffect(() => {
    const mainColumn = mainColumnMarker?.parentElement;
    const sideColumn = sideColumnMarker?.parentElement;

    const apply = (next) => {
      const rounded = Math.round(next);
      if (Math.abs(rounded - heightRef.current) < 2) return; // ignore sub-pixel noise, avoids a resize loop
      heightRef.current = rounded;
      setHeight(rounded);
    };

    if (!enabled || !mainColumn || !sideColumn || typeof ResizeObserver === 'undefined') {
      apply(defaultHeight);
      return undefined;
    }

    const measure = () => {
      const block = sideColumn.querySelector(blockSelector);
      const mainRect = mainColumn.getBoundingClientRect();
      const sideRect = sideColumn.getBoundingClientRect();
      const blockHeight = block ? block.getBoundingClientRect().height : 0;
      const stacked = sideRect.top >= mainRect.bottom - 1;
      if (!block || !blockHeight || stacked || mainRect.height === 0) return apply(defaultHeight);
      apply(Math.max(defaultHeight, mainRect.height - (sideRect.height - blockHeight)));
    };

    // ResizeObserver callbacks run after layout; the state update renders in a later frame, so this does not loop
    const observer = new ResizeObserver(measure);
    observer.observe(mainColumn);
    observer.observe(sideColumn);
    measure();
    return () => observer.disconnect();
  }, [mainColumnMarker, sideColumnMarker, defaultHeight, blockSelector, enabled]);

  return { mainColumnMarkerRef, sideColumnMarkerRef, height };
}
